import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Header.css';
import LoginModal from '../LoginModal';
import RegisterModal from '../RegisterModal';
import VerifyEmailModal from '../VerifyEmailModal';
import RequirementModal from '../RequirementModal';
import CategorySidebar from '../CategorySidebar';
import { useAuth } from '../../context/AuthContext';
import { useSpecialistAuth } from '../../context/SpecialistAuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL, SOCKET_URL } from '../../config/api';
import NotificationBell from '../NotificationBell/NotificationBell';
import axios from 'axios';

// Helper to get proper image URL
const getImageUrl = (url) => {
  if (!url) return '';
  // Fix localhost URLs from old data
  if (url.startsWith('http://localhost')) {
    try { return `${SOCKET_URL}${new URL(url).pathname}`; } catch { return url; }
  }
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${SOCKET_URL}${url}`;
  return url;
};

const Header = ({ isHomePage = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCurrency, currencies, changeCurrency } = useCurrency();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const searchTimerRef = useRef(null);
  const userMenuRef = useRef(null);
  const userDropdownRef = useRef(null);
  const currencyRef = useRef(null);
  const languageRef = useRef(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isVerifyEmailModalOpen, setIsVerifyEmailModalOpen] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [isRFQModalOpen, setIsRFQModalOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categories, setCategories] = useState([]);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showMobileProfileDropdown, setShowMobileProfileDropdown] = useState(false);
  const [expandedMobileCategory, setExpandedMobileCategory] = useState(null);
  const [loginRedirectPath, setLoginRedirectPath] = useState(null);
  const [showMobileLanguage, setShowMobileLanguage] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState({ 
    code: i18n.language || 'en', 
    name: i18n.language === 'ar' ? 'العربية' : 'English', 
    flag: i18n.language === 'ar' ? 'sa' : 'us' 
  });

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setShowMobileSearch(false);
  };

  const toggleMobileSearch = () => {
    setShowMobileSearch(!showMobileSearch);
    setIsMobileMenuOpen(false);
  };

  const handleMobileCategoryClick = (category) => {
    if (expandedMobileCategory?.id === category.id) {
      setExpandedMobileCategory(null);
    } else {
      setExpandedMobileCategory(category);
    }
  };

  const handleMobileCategoryNavigate = (category) => {
    setIsMobileMenuOpen(false);
    setExpandedMobileCategory(null);
    navigate(`/products?category=${category.id}`);
  };

  const handleMobileSubcategoryClick = (category, subcategory) => {
    setIsMobileMenuOpen(false);
    setExpandedMobileCategory(null);
    navigate(`/products?category=${subcategory.id || category.id}`);
  };

  const handleMobileNavClick = (path) => {
    setIsMobileMenuOpen(false);
    navigate(path);
  };

  // Handle currency selection
  const handleCurrencySelect = (currencyCode) => {
    changeCurrency(currencyCode);
    setShowCurrencyDropdown(false);
  };

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to hardcoded categories if API fails
        setCategories([
          { id: 1, name: 'Building Materials', subcategories: [{ name: 'Cement' }, { name: 'Steel' }, { name: 'Bricks' }, { name: 'Sand' }] },
          { id: 2, name: 'Structural Work', subcategories: [{ name: 'Beams' }, { name: 'Columns' }, { name: 'Foundations' }] },
          { id: 3, name: 'Interior & Finishing', subcategories: [{ name: 'Paints' }, { name: 'Tiles' }, { name: 'Flooring' }] },
          { id: 4, name: 'Plumbing & Sanitary', subcategories: [{ name: 'Pipes' }, { name: 'Fittings' }, { name: 'Sanitary Ware' }] },
          { id: 5, name: 'Electrical Works', subcategories: [{ name: 'Wiring' }, { name: 'Switches' }, { name: 'Lighting' }] },
          { id: 6, name: 'Windows & Doors', subcategories: [{ name: 'Aluminum' }, { name: 'Wood' }, { name: 'Glass' }] },
          { id: 7, name: 'Green Building Materials', subcategories: [{ name: 'Solar Panels' }, { name: 'Insulation' }] },
          { id: 8, name: 'Scaffolding & Shuttering', subcategories: [{ name: 'Steel Scaffolding' }, { name: 'Aluminum' }] },
        ]);
      }
    };
    fetchCategories();
  }, []);

  const handleCategoryClick = (category) => {
    setShowCategoryDropdown(false);
    setHoveredCategory(null);
    navigate(`/products?category=${category.id}`);
  };

  const handleSubcategoryClick = (category, subcategory) => {
    setShowCategoryDropdown(false);
    setHoveredCategory(null);
    navigate(`/products?category=${subcategory.id || category.id}`);
  };

  const languages = [
    { code: 'en', name: 'English', flag: 'us' },
    { code: 'ar', name: 'العربية', flag: 'sa' }
  ];

  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language);
    setShowLanguageDropdown(false);
    i18n.changeLanguage(language.code);
    localStorage.setItem('language', language.code);
    // Set document direction for RTL languages
    document.documentElement.dir = language.code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language.code;
  };

  const { user, isAuthenticated, isAdmin, isSupplier, logout } = useAuth();
  const { specialist, isAuthenticated: isSpecialistAuthenticated, logout: specialistLogout } = useSpecialistAuth();

  // Combined authentication state - user is logged in if either regular or specialist auth
  const isLoggedIn = isAuthenticated || isSpecialistAuthenticated;
  const isSpecialist = isSpecialistAuthenticated && !isAuthenticated;

  // Login/Register Modal handlers
  const openLoginModal = () => {
    setIsRegisterModalOpen(false);
    setIsVerifyEmailModalOpen(false);
    setIsLoginModalOpen(true);
  };

  const openRegisterModal = () => {
    setIsLoginModalOpen(false);
    setIsVerifyEmailModalOpen(false);
    setIsRegisterModalOpen(true);
  };

  const closeModals = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(false);
    setIsVerifyEmailModalOpen(false);
  };

  const handleVerifyEmail = (email) => {
    setRegisteredEmail(email);
    setIsRegisterModalOpen(false);
    setIsVerifyEmailModalOpen(true);
  };

  const handleVerifyEmailContinue = () => {
    setIsVerifyEmailModalOpen(false);
    // User is already logged in after registration
  };

  const handleLogout = () => {
    // Clear all auth sessions completely
    if (isSpecialistAuthenticated) {
      specialistLogout();
    }
    if (isAuthenticated) {
      logout();
    }
    // Safety net: force-clear all auth storage regardless
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('specialist_token');
    localStorage.removeItem('specialist');
    setShowUserMenu(false);
    navigate('/');
  };

  // Search functionality
  const handleSearch = () => {
    if (searchTerm.trim()) {
      setShowSearchDropdown(false);
      navigate(`/products?search=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Debounced search suggestions
  const fetchSearchSuggestions = useCallback((term) => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    if (!term || term.trim().length < 2) {
      setSearchSuggestions([]);
      setSupplierSuggestions([]);
      setShowSearchDropdown(false);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/products/search-suggestions?q=${encodeURIComponent(term.trim())}`);

        setSearchSuggestions(res.data.suggestions || []);
        setSupplierSuggestions(res.data.suppliers || []);
        setShowSearchDropdown(true);
      } catch (err) {
        console.error('Search suggestions error:', err);
        setSearchSuggestions([]);
        setSupplierSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    fetchSearchSuggestions(val);
  };

  const handleSuggestionClick = (suggestion) => {
    setShowSearchDropdown(false);
    setSearchTerm('');
    // Navigate to the subcategory page if available, else category page
    if (suggestion.categorySlug && suggestion.subcategorySlug) {
      navigate(`/category/${suggestion.categorySlug}/${suggestion.subcategorySlug}`);
    } else if (suggestion.categorySlug) {
      navigate(`/category/${suggestion.categorySlug}`);
    } else {
      navigate(`/product/${suggestion.id}`);
    }
  };

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inDesktop = searchRef.current && searchRef.current.contains(e.target);
      const inMobile = mobileSearchRef.current && mobileSearchRef.current.contains(e.target);
      if (!inDesktop && !inMobile) {
        setShowSearchDropdown(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target) && (!userDropdownRef.current || !userDropdownRef.current.contains(e.target))) {
        // Also check if click is inside a portal dropdown by class
        if (!e.target.closest || !e.target.closest('.user-dropdown-menu')) {
          setShowUserMenu(false);
        }
      }
      if (currencyRef.current && !currencyRef.current.contains(e.target)) {
        setShowCurrencyDropdown(false);
      }
      if (languageRef.current && !languageRef.current.contains(e.target)) {
        setShowLanguageDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openRFQModal = () => {
    if (!isLoggedIn) {
      setLoginRedirectPath('/send-inquire');
      setIsLoginModalOpen(true);
      return;
    }
    setIsRFQModalOpen(true);
  };

  const closeRFQModal = () => {
    setIsRFQModalOpen(false);
  };

  return (
    <header className="header">
      {/* ========== MOBILE HEADER ========== */}
      <div className="mobile-header">
        {/* Mobile Top Bar */}
        <div className="mobile-header-top">
          <button className="mobile-menu-btn" onClick={toggleMobileMenu} aria-label="Menu">
            {isMobileMenuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="#1A1A1A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <img src="/mobile-icons/hamburger-menu.png" alt="Menu" className="hamburger-icon-img" />
            )}
          </button>

          <NavLink to="/" className="mobile-logo">
            <img src="/logo.png?v=2" alt="Conflux" />
          </NavLink>

          <div className="mobile-header-actions">
            <div className="mobile-profile-wrapper">
              <button 
                className="mobile-login-btn" 
                onClick={() => setShowMobileProfileDropdown(!showMobileProfileDropdown)} 
                aria-label="Profile"
              >
                {isLoggedIn && (user?.companyLogo || user?.avatar || isSpecialist) ? (
                  <div className="mobile-user-avatar">
                    {isSpecialist ? (
                      <svg width="24" height="24" viewBox="0 0 36 36" fill="none">
                        <path d="M18 3.37109C20.2173 3.37109 22.3741 4.09847 24.1377 5.44238C25.9013 6.78629 27.1745 8.67272 27.7627 10.8105C28.3507 12.9481 28.2213 15.2197 27.3936 17.2764C26.5656 19.3333 25.0849 21.0621 23.1797 22.1963C27.0314 23.4549 30.3319 26.1099 32.4736 29.8125C32.5521 29.9405 32.6044 30.083 32.627 30.2314C32.6495 30.3798 32.6423 30.5313 32.6055 30.6768C32.5686 30.8222 32.5026 30.9594 32.4121 31.0791C32.3215 31.1989 32.2072 31.2999 32.0771 31.375C31.9471 31.4501 31.8033 31.4979 31.6543 31.5166C31.5052 31.5353 31.3536 31.5242 31.209 31.4834C31.0645 31.4426 30.9298 31.373 30.8125 31.2793C30.6952 31.1856 30.598 31.0694 30.5264 30.9375C27.877 26.3587 23.1933 23.625 18 23.625C12.8069 23.6252 8.12391 26.3589 5.47461 30.9375C5.40295 31.0694 5.30574 31.1856 5.18848 31.2793C5.07121 31.373 4.93644 31.4426 4.79199 31.4834C4.64741 31.5242 4.49573 31.5353 4.34668 31.5166C4.19771 31.498 4.05385 31.4501 3.92383 31.375C3.79374 31.2999 3.67951 31.1989 3.58887 31.0791C3.49835 30.9594 3.43241 30.8222 3.39551 30.6768C3.35868 30.5313 3.35147 30.3798 3.37402 30.2314C3.3966 30.083 3.44883 29.9405 3.52734 29.8125C5.66906 26.1084 8.96957 23.4535 12.8213 22.1963C10.9161 21.0621 9.43536 19.3333 8.60742 17.2764C7.7797 15.2197 7.65023 12.9481 8.23828 10.8105C8.82645 8.6727 10.0997 6.78629 11.8633 5.44238C13.6267 4.09857 15.7829 3.37119 18 3.37109Z" fill="#FF6B00"/>
                      </svg>
                    ) : (
                      <img src={getImageUrl(user.companyLogo || user.avatar)} alt="User" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                  </div>
                ) : (
                  <img src="/mobile-icons/right-sidebar.png" alt="Profile" className="mobile-profile-icon-img" />
                )}
              </button>
              
              {/* Mobile Profile Dropdown */}
              {showMobileProfileDropdown && (
                <>
                  <div className="mobile-profile-overlay" onClick={() => setShowMobileProfileDropdown(false)}></div>
                  <div className="mobile-profile-dropdown">
                    {!isLoggedIn && (
                      <div className="mobile-profile-auth">
                        <button className="mobile-profile-signin" onClick={() => { setShowMobileProfileDropdown(false); openLoginModal(); }}>Sign In</button>
                        <button className="mobile-profile-joinfree" onClick={() => { setShowMobileProfileDropdown(false); openRegisterModal(); }}>Join Free</button>
                      </div>
                    )}
                    
                    <div className="mobile-profile-menu">
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isLoggedIn) {
                          navigate(isSpecialist ? '/specialist-messages' : (isAdmin ? '/admin/messages' : '/messages'));
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M22 6L12 13L2 6" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Inquiries</span>
                      </button>
                      
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isLoggedIn) {
                          navigate(isSpecialist ? '/specialist-messages' : (isAdmin ? '/admin/messages' : '/messages'));
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Chats</span>
                      </button>
                      
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isLoggedIn) {
                          navigate(isSpecialist ? '/specialist-rfq' : '/rfq');
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 2V8H20" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 13H8" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 17H8" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10 9H9H8" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Quotations</span>
                      </button>
                      
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isLoggedIn) {
                          navigate('/favorites');
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Favorites</span>
                      </button>
                      
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isLoggedIn) {
                          navigate('/following');
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.0329 6.39464C21.7563 5.72718 21.351 5.12075 20.84 4.61Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Follow</span>
                      </button>
                      
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isLoggedIn) {
                          setIsRFQModalOpen(true);
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Post Sourcing Request</span>
                      </button>
                      
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isLoggedIn) {
                          navigate('/my-support');
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="#333" strokeWidth="1.5"/>
                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="12" y1="17" x2="12.01" y2="17" stroke="#333" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Support</span>
                      </button>
                      
                      {isLoggedIn && (
                        <button className="mobile-profile-logout" onClick={() => { setShowMobileProfileDropdown(false); handleLogout(); }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16 17L21 12L16 7" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M21 12H9" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Logout
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Search Bar - Always Visible */}
        <div className="mobile-search-container" ref={mobileSearchRef}>
          <div className="mobile-search-wrapper">
            <input
              type="text"
              placeholder={t('header.searchPlaceholder') || 'Enter your keywords.....'}
              value={searchTerm}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
              onFocus={() => { if (searchSuggestions.length > 0 || supplierSuggestions.length > 0) setShowSearchDropdown(true); }}
              className="mobile-search-input"
              autoComplete="off"
            />
            <button className="mobile-search-submit" onClick={handleSearch}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          {/* Mobile Search Suggestions Dropdown */}
          {showSearchDropdown && (
            <div className="search-suggestions-dropdown mobile-search-suggestions">
              {isSearching ? (
                <div className="search-suggestion-item search-loading">Searching...</div>
              ) : (searchSuggestions.length > 0 || supplierSuggestions.length > 0) ? (
                <>
                  {supplierSuggestions.length > 0 && (
                    <>
                      <div className="search-suggestion-section-title">Suppliers</div>
                      {supplierSuggestions.map((item) => (
                        <div
                          key={`supplier-${item.id}`}
                          className="search-suggestion-item search-suggestion-supplier"
                          onClick={() => { setShowSearchDropdown(false); setSearchTerm(''); navigate(`/seller/${item.id}`); }}
                        >
                          {item.logo ? (
                            <img src={item.logo.startsWith('http') ? item.logo : `${SOCKET_URL}${item.logo}`} alt={item.name} className="search-suggestion-img" />
                          ) : (
                            <div className="search-suggestion-supplier-avatar">{item.name?.charAt(0) || 'S'}</div>
                          )}
                          <div className="search-suggestion-info">
                            <div className="search-suggestion-name">
                              {item.name}
                              {item.isVerified && <span className="search-verified-badge">✓</span>}
                            </div>
                            <div className="search-suggestion-category">
                              {item.businessType}{item.country ? ` · ${item.country}` : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {searchSuggestions.length > 0 && (
                    <>
                      {supplierSuggestions.length > 0 && <div className="search-suggestion-section-title">Products</div>}
                      {searchSuggestions.map((item) => (
                        <div
                          key={item.id}
                          className="search-suggestion-item"
                          onClick={() => handleSuggestionClick(item)}
                        >
                          {item.image && (
                            <img
                              src={item.image.startsWith('http') ? item.image : `${SOCKET_URL}${item.image}`}
                              alt={item.name}
                              className="search-suggestion-img"
                            />
                          )}
                          <div className="search-suggestion-info">
                            <div className="search-suggestion-name">{item.name}</div>
                            <div className="search-suggestion-category">
                              {item.categoryName}{item.subcategoryName ? ` > ${item.subcategoryName}` : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="search-suggestion-item search-view-all" onClick={handleSearch}>
                    View all results for "{searchTerm}"
                  </div>
                </>
              ) : (
                <div className="search-suggestion-item search-no-results">No results found</div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Navigation Drawer */}
        <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={toggleMobileMenu}></div>
        <nav className={`mobile-nav-drawer ${isMobileMenuOpen ? 'open' : ''}`}>
          {/* Mobile Categories Section - First */}
          <div className="mobile-sidebar-section">
            <div className="mobile-sidebar-header">
              <img src="/mobile-icons/hamburger-menu.png" alt="Menu" className="sidebar-hamburger-icon" />
              <span>Categories</span>
            </div>
            <div className="mobile-sidebar-list">
              {categories.slice(0, 7).map((category) => (
                <div 
                  key={category.id || category.name} 
                  className="mobile-sidebar-item"
                  onClick={() => handleMobileCategoryNavigate(category)}
                >
                  {category.name}
                </div>
              ))}
              <NavLink 
                to="/all-categories" 
                className="mobile-sidebar-item mobile-sidebar-viewmore" 
                onClick={() => handleMobileNavClick('/all-categories')}
              >
                View More
              </NavLink>
            </div>
          </div>

          {/* Divider */}
          <div className="mobile-sidebar-divider"></div>

          {/* Quick Links Section */}
          <div className="mobile-sidebar-section">
            <div 
              className="mobile-sidebar-link"
              onClick={() => { setIsMobileMenuOpen(false); openRFQModal(); }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#333" strokeWidth="2"/>
                <path d="M2 12H22M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="#333" strokeWidth="2"/>
              </svg>
              <span>Post Sourcing Request</span>
            </div>
            <div 
              className="mobile-sidebar-link"
              onClick={() => handleMobileNavClick('/get-app')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="5" y="2" width="14" height="20" rx="2" stroke="#333" strokeWidth="2"/>
                <path d="M12 18H12.01" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Get App</span>
            </div>
            <div 
              className="mobile-sidebar-link mobile-sidebar-link-arrow"
              onClick={() => setShowMobileLanguage(!showMobileLanguage)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#333" strokeWidth="2"/>
                <path d="M2 12H22M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="#333" strokeWidth="2"/>
              </svg>
              <span>{selectedLanguage.name}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="mobile-sidebar-arrow">
                <path d="M9 18L15 12L9 6" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            {showMobileLanguage && (
              <div className="mobile-sidebar-suboptions">
                {languages.map((lang) => (
                  <div 
                    key={lang.code}
                    className={`mobile-sidebar-suboption ${selectedLanguage.code === lang.code ? 'active' : ''}`}
                    onClick={() => { handleLanguageSelect(lang); setShowMobileLanguage(false); }}
                  >
                    <img src={`https://flagcdn.com/w80/${lang.flag}.png`} alt={lang.name} className="mobile-sidebar-flag" />
                    <span>{lang.name}</span>
                  </div>
                ))}
              </div>
            )}
            <div 
              className="mobile-sidebar-link"
              onClick={() => handleMobileNavClick('/contact')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7294C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1469 21.5901 20.9046 21.7335 20.6408 21.8227C20.377 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77383 17.3147 6.72534 15.2662 5.19001 12.85C3.49998 10.2412 2.44824 7.27099 2.12001 4.18C2.09501 3.90347 2.12789 3.62476 2.21649 3.36162C2.30508 3.09849 2.44756 2.85669 2.63477 2.65162C2.82197 2.44655 3.04981 2.28271 3.30379 2.17052C3.55778 2.05833 3.83234 2.00026 4.11001 2H7.11001C7.5953 1.99522 8.06579 2.16708 8.43376 2.48353C8.80173 2.79999 9.04208 3.23945 9.11001 3.72C9.23662 4.68007 9.47145 5.62273 9.81001 6.53C9.94455 6.88792 9.97366 7.27691 9.89391 7.65088C9.81415 8.02485 9.62886 8.36811 9.36001 8.64L8.09001 9.91C9.51356 12.4135 11.5865 14.4864 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9752 14.1859 16.3491 14.1061C16.7231 14.0263 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Contact Us</span>
            </div>
          </div>

          {/* Divider */}
          <div className="mobile-sidebar-divider"></div>

          {/* Footer Links */}
          <div className="mobile-sidebar-section mobile-sidebar-footer">
            <NavLink 
              to="/user-agreement" 
              className="mobile-sidebar-footer-link"
              onClick={() => handleMobileNavClick('/user-agreement')}
            >
              User Agreement
            </NavLink>
            <NavLink 
              to="/privacy-policy" 
              className="mobile-sidebar-footer-link"
              onClick={() => handleMobileNavClick('/privacy-policy')}
            >
              Privacy Policy
            </NavLink>
          </div>
        </nav>
      </div>

      {/* ========== DESKTOP HEADER ========== */}
      {/* Top Promotional Banner */}
      <div className="promo-top-banner desktop-only">
        <div className="promo-top-banner-content">
          <span className="promo-top-banner-text">
            <span role="img" aria-label="heart">❤️</span> Valentine's Sale
          </span>
          <span className="promo-top-banner-highlight">Jewelry, Watches & Fashion Accessories</span>
          <span className="promo-top-banner-offer">Indulge yourself with up to 50% off</span>
          <button className="promo-top-banner-btn" onClick={() => navigate('/products')}>SHOP NOW</button>
        </div>
      </div>

      {/* Secondary Navigation Bar */}
      <div className="secondary-nav desktop-only">
        <div className="secondary-nav-content">
          <NavLink to="/products" className="secondary-nav-link">Products</NavLink>
          <NavLink to="/top-seller" className="secondary-nav-link">Manufacturers</NavLink>
          <NavLink to="/send-inquire" className="secondary-nav-link">Source PRO</NavLink>
          <NavLink to="/buy-leads" className="secondary-nav-link">Classified</NavLink>
          <NavLink to="/submit-rfq" className="secondary-nav-link">Submit RFQ</NavLink>
        </div>
      </div>

      {/* Top Navbar */}
      <div className="header-top desktop-only">
        {/* Top Row - Currency and Language */}
        <div className="header-top-row">
          <div className="currency-selector" ref={currencyRef} onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}>
            <span>{selectedCurrency.code} {selectedCurrency.symbol}</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 9.5L12 15.5L6 9.5H18Z" fill="#FF6B00" stroke="#FF6B00" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
            {showCurrencyDropdown && (
              <div className="currency-dropdown">
                {currencies.map((curr) => (
                  <div 
                    key={curr.code} 
                    className={`currency-option ${selectedCurrency.code === curr.code ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleCurrencySelect(curr.code); }}
                  >
                    <span className="currency-code">{curr.code}</span>
                    <span className="currency-symbol">{curr.symbol}</span>
                    <span className="currency-name">{curr.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="language-selector" ref={languageRef} onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
            <div className="flag-container">
              <img src={`https://flagcdn.com/w80/${selectedLanguage.flag}.png`} alt="Flag" className="flag-icon" />
            </div>
            <div className="language-text-wrapper">
              <span>{selectedLanguage.name}</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 9.5L12 15.5L6 9.5H18Z" fill="#FF6B00"/>
              </svg>
            </div>
            {showLanguageDropdown && (
              <div className="language-dropdown">
                {languages.map((lang) => (
                  <div 
                    key={lang.code} 
                    className="language-option"
                    onClick={(e) => { e.stopPropagation(); handleLanguageSelect(lang); }}
                  >
                    <img src={`https://flagcdn.com/w80/${lang.flag}.png`} alt={lang.name} className="dropdown-flag" />
                    <span>{lang.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Main Row - Logo, Search, Actions */}
        <div className="header-main-row">
          <div className="logo-container">
            <NavLink to="/">
              <img src="/logo.png?v=2" alt="Conflux" className="header-logo" />
            </NavLink>
          </div>
          
          <div className="search-wrapper" ref={searchRef}>
            <div className="search-container">
              <div className="search-input-wrapper">
                <input 
                  type="text" 
                  placeholder={t('header.searchPlaceholder')} 
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  onKeyPress={handleSearchKeyPress}
                  onFocus={() => { if (searchSuggestions.length > 0 || supplierSuggestions.length > 0) setShowSearchDropdown(true); }}
                  className="search-input"
                  autoComplete="off"
                />
              </div>
              <button className="search-button" onClick={handleSearch}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M22.5 22L15.8333 15.3333M2.5 9.77778C2.5 10.7992 2.70118 11.8106 3.09205 12.7542C3.48292 13.6978 4.05582 14.5553 4.77806 15.2775C5.50029 15.9997 6.35771 16.5726 7.30135 16.9635C8.24499 17.3544 9.25639 17.5556 10.2778 17.5556C11.2992 17.5556 12.3106 17.3544 13.2542 16.9635C14.1978 16.5726 15.0553 15.9997 15.7775 15.2775C16.4997 14.5553 17.0726 13.6978 17.4635 12.7542C17.8544 11.8106 18.0556 10.7992 18.0556 9.77778C18.0556 8.75639 17.8544 7.74499 17.4635 6.80135C17.0726 5.85771 16.4997 5.00029 15.7775 4.27806C15.0553 3.55582 14.1978 2.98292 13.2542 2.59205C12.3106 2.20118 11.2992 2 10.2778 2C9.25639 2 8.24499 2.20118 7.30135 2.59205C6.35771 2.98292 5.50029 3.55582 4.77806 4.27806C4.05582 5.00029 3.48292 5.85771 3.09205 6.80135C2.70118 7.74499 2.5 8.75639 2.5 9.77778Z" stroke="white" strokeOpacity="0.8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>SEARCH</span>
              </button>
            </div>
            {/* Search Suggestions Dropdown */}
            {showSearchDropdown && (
              <div className="search-suggestions-dropdown">
                {isSearching ? (
                  <div className="search-suggestion-item search-loading">Searching...</div>
                ) : (searchSuggestions.length > 0 || supplierSuggestions.length > 0) ? (
                  <>
                    {supplierSuggestions.length > 0 && (
                      <>
                        <div className="search-suggestion-section-title">Suppliers</div>
                        {supplierSuggestions.map((item) => (
                          <div
                            key={`supplier-${item.id}`}
                            className="search-suggestion-item search-suggestion-supplier"
                            onClick={() => { setShowSearchDropdown(false); setSearchTerm(''); navigate(`/seller/${item.id}`); }}
                          >
                            {item.logo ? (
                              <img src={item.logo.startsWith('http') ? item.logo : `${SOCKET_URL}${item.logo}`} alt={item.name} className="search-suggestion-img" />
                            ) : (
                              <div className="search-suggestion-supplier-avatar">{item.name?.charAt(0) || 'S'}</div>
                            )}
                            <div className="search-suggestion-info">
                              <div className="search-suggestion-name">
                                {item.name}
                                {item.isVerified && <span className="search-verified-badge">✓</span>}
                              </div>
                              <div className="search-suggestion-category">
                                {item.businessType}{item.country ? ` · ${item.country}` : ''}
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    {searchSuggestions.length > 0 && (
                      <>
                        {supplierSuggestions.length > 0 && <div className="search-suggestion-section-title">Products</div>}
                        {searchSuggestions.map((item) => (
                          <div
                            key={item.id}
                            className="search-suggestion-item"
                            onClick={() => handleSuggestionClick(item)}
                          >
                            {item.image && (
                              <img
                                src={item.image.startsWith('http') ? item.image : `${SOCKET_URL}${item.image}`}
                                alt={item.name}
                                className="search-suggestion-img"
                              />
                            )}
                            <div className="search-suggestion-info">
                              <div className="search-suggestion-name">{item.name}</div>
                              <div className="search-suggestion-category">
                                {item.categoryName}{item.subcategoryName ? ` > ${item.subcategoryName}` : ''}
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    <div className="search-suggestion-item search-view-all" onClick={handleSearch}>
                      View all results for "{searchTerm}"
                    </div>
                  </>
                ) : (
                  <div className="search-suggestion-item search-no-results">No results found</div>
                )}
              </div>
            )}
          </div>
          
          <div className="header-actions">
            {isHomePage ? (
              <>
                {/* Homepage: compact action icons */}
                <NavLink to="/documents-submit" className="action-item hp-action-link" style={{ textDecoration: 'none' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                  <span>Become a<br/>Supplier</span>
                </NavLink>
                <NavLink to="/submit-rfq" className="action-item hp-action-link" style={{ textDecoration: 'none' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                  <span>Request for<br/>Quotations</span>
                </NavLink>
                <NavLink to="/messages" className="action-item hp-icon-only" style={{ textDecoration: 'none' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </NavLink>
                <NavLink to="/favorites" className="action-item hp-icon-only" style={{ textDecoration: 'none' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </NavLink>
                <div className="action-item hp-icon-only" ref={userMenuRef} style={{ cursor: 'pointer' }} onClick={() => {
                  if (isLoggedIn) {
                    setShowUserMenu(!showUserMenu);
                  } else {
                    openLoginModal();
                  }
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1F396C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2.5 4L5 6.5L7.5 4" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {showUserMenu && isLoggedIn && ReactDOM.createPortal(
                  <>
                    <div onClick={(e) => { e.stopPropagation(); setShowUserMenu(false); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }} />
                    <div className="user-dropdown-menu" ref={userDropdownRef} onClick={(e) => e.stopPropagation()} style={{
                      position: 'fixed',
                      top: userMenuRef.current ? userMenuRef.current.getBoundingClientRect().bottom + 8 : 120,
                      left: userMenuRef.current ? userMenuRef.current.getBoundingClientRect().left - 100 : 'auto',
                      zIndex: 99999,
                      backgroundColor: '#ffffff',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.1)',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      minWidth: '200px',
                      padding: '6px 0',
                      opacity: 1,
                    }}>
                      <div className="user-dropdown-item" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setShowUserMenu(false); navigate(isSpecialist ? '/specialist-dashboard' : (isSupplier ? '/vendor-dashboard' : (isAdmin ? '/admin/dashboard' : '/vendor-dashboard'))); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                        <span>Dashboard</span>
                      </div>
                      <div className="user-dropdown-item" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setShowUserMenu(false); navigate('/my-support'); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        <span>Support</span>
                      </div>
                      <div className="user-dropdown-divider"></div>
                      <button className="user-dropdown-item user-dropdown-logout" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setShowUserMenu(false); handleLogout(); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                        <span>Logout</span>
                      </button>
                    </div>
                  </>,
                  document.body
                )}
              </>
            ) : (
              <>
            {/* My Account Icon */}
            {isLoggedIn ? (
              <>
              <div className="action-item user-menu-container" ref={userMenuRef} style={{ cursor: 'pointer', position: 'relative' }} onClick={() => setShowUserMenu(!showUserMenu)}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <span style={{ color: '#FF6B00' }}>MY ACCOUNT</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '2px' }}>
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </div>
              {showUserMenu && ReactDOM.createPortal(
                <>
                  <div onClick={(e) => { e.stopPropagation(); setShowUserMenu(false); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998, background: 'transparent' }} />
                  <div className="user-dropdown-menu" ref={userDropdownRef} onClick={(e) => e.stopPropagation()} style={{
                    position: 'fixed',
                    top: userMenuRef.current ? userMenuRef.current.getBoundingClientRect().bottom + 8 : 120,
                    left: userMenuRef.current ? userMenuRef.current.getBoundingClientRect().left : 'auto',
                    zIndex: 99999,
                    backgroundColor: '#ffffff',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.1)',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    minWidth: '200px',
                    padding: '6px 0',
                    opacity: 1,
                    pointerEvents: 'auto',
                  }}>
                    <div className="user-dropdown-item" style={{ cursor: 'pointer', pointerEvents: 'auto' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowUserMenu(false); navigate(isSpecialist ? '/specialist-dashboard' : (isSupplier ? '/vendor-dashboard' : (isAdmin ? '/admin/dashboard' : '/vendor-dashboard'))); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                      <span>Dashboard</span>
                    </div>
                    <div className="user-dropdown-item" style={{ cursor: 'pointer', pointerEvents: 'auto' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowUserMenu(false); navigate('/my-support'); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                      <span>Support</span>
                    </div>
                    <div className="user-dropdown-item" style={{ cursor: 'pointer', pointerEvents: 'auto' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowUserMenu(false); navigate('/favorites'); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                      <span>Favourites</span>
                    </div>
                    <div className="user-dropdown-item" style={{ cursor: 'pointer', pointerEvents: 'auto' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowUserMenu(false); navigate('/messages'); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                      <span>Messages</span>
                    </div>
                    <div className="user-dropdown-divider"></div>
                    <button className="user-dropdown-item user-dropdown-logout" style={{ cursor: 'pointer', pointerEvents: 'auto' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowUserMenu(false); handleLogout(); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      <span>Logout</span>
                    </button>
                  </div>
                </>,
                document.body
              )}
              </>
            ) : (
              <>
                <div className="action-item" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowUserMenu(prev => !prev); }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span>My Account</span>
                </div>
                {showUserMenu && ReactDOM.createPortal(
                  <>
                    <div onClick={(e) => { e.stopPropagation(); setShowUserMenu(false); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }} />
                    <div ref={userMenuRef} onClick={(e) => e.stopPropagation()} style={{
                      position: 'fixed',
                      top: '120px',
                      right: '80px',
                      background: '#fff',
                      borderRadius: '12px',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                      border: '1px solid #e2e8f0',
                      minWidth: '200px',
                      padding: '6px 0',
                      zIndex: 99999,
                      animation: 'slideDown 0.2s ease'
                    }}>
                      <button className="user-dropdown-item" onClick={(e) => { e.stopPropagation(); setShowUserMenu(false); openLoginModal(); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                        <span>Sign In</span>
                      </button>
                      <button className="user-dropdown-item" onClick={(e) => { e.stopPropagation(); setShowUserMenu(false); openRegisterModal(); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
                        <span>Register</span>
                      </button>
                      <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }}></div>
                      <NavLink to="/my-support" className="user-dropdown-item" style={{ pointerEvents: 'auto' }} onClick={(e) => { e.stopPropagation(); setShowUserMenu(false); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        <span>Support</span>
                      </NavLink>
                    </div>
                  </>,
                  document.body
                )}
              </>
            )}
            
            {/* Notification Bell */}
            <NotificationBell />

            {/* Messages Icon */}
            <NavLink to="/messages" className="action-item" style={{ cursor: 'pointer', textDecoration: 'none' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <span>Messages</span>
            </NavLink>
            
            {/* Inquiries Icon */}
            <NavLink to="/send-inquire" className="action-item" style={{ cursor: 'pointer', textDecoration: 'none' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <span>Inquiries</span>
            </NavLink>
            
            {/* Favourite Icon */}
            <NavLink to="/favorites" className="action-item" style={{ cursor: 'pointer', textDecoration: 'none' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              <span>Favourite</span>
            </NavLink>

            {/* Support Icon */}
            <NavLink to="/my-support" className="action-item" style={{ cursor: 'pointer', textDecoration: 'none' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>Support</span>
            </NavLink>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Navigation Bar */}
      <nav className={`navbar desktop-only ${isHomePage ? 'homepage-navbar' : ''}`}>
        <div className={`navbar-content ${isHomePage ? 'homepage-grid-layout' : ''}`}>
          {/* Category dropdown - works on all pages */}
          <div 
            className="category-dropdown" 
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
          >
            <svg className="category-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 3H4C3.73478 3 3.48043 3.10536 3.29289 3.29289C3.10536 3.48043 3 3.73478 3 4V10C3 10.2652 3.10536 10.5196 3.29289 10.7071C3.48043 10.8946 3.73478 11 4 11H10C10.2652 11 10.5196 10.8946 10.7071 10.7071C10.8946 10.5196 11 10.2652 11 10V4C11 3.73478 10.8946 3.48043 10.7071 3.29289C10.5196 3.10536 10.2652 3 10 3ZM20 3H14C13.7348 3 13.4804 3.10536 13.2929 3.29289C13.1054 3.48043 13 3.73478 13 4V10C13 10.2652 13.1054 10.5196 13.2929 10.7071C13.4804 10.8946 13.7348 11 14 11H20C20.2652 11 20.5196 10.8946 20.7071 10.7071C20.8946 10.5196 21 10.2652 21 10V4C21 3.73478 20.8946 3.48043 20.7071 3.29289C20.5196 3.10536 20.2652 3 20 3ZM10 13H4C3.73478 13 3.48043 13.1054 3.29289 13.2929C3.10536 13.4804 3 13.7348 3 14V20C3 20.2652 3.10536 20.5196 3.29289 20.7071C3.48043 20.8946 3.73478 21 4 21H10C10.2652 21 10.5196 20.8946 10.7071 20.7071C10.8946 20.5196 11 20.2652 11 20V14C11 13.7348 10.8946 13.4804 10.7071 13.2929C10.5196 13.1054 10.2652 13 10 13ZM17 13C17.7826 13 18.548 13.2296 19.2014 13.6603C19.8549 14.091 20.3676 14.7039 20.6761 15.4232C20.9846 16.1424 21.0753 16.9363 20.9371 17.7066C20.7988 18.4769 20.4376 19.1898 19.8983 19.7568C19.3589 20.3239 18.665 20.7202 17.9026 20.8968C17.1402 21.0734 16.3427 21.0225 15.6089 20.7503C14.8752 20.4782 14.2374 19.9967 13.7745 19.3657C13.3117 18.7346 13.0441 17.9816 13.005 17.2L13 17L13.005 16.8C13.0563 15.775 13.4996 14.809 14.2432 14.1017C14.9868 13.3944 15.9738 13 17 13Z" fill="#FF6B00"/>
            </svg>
            <span className="category-text">All Categories</span>
            <svg className={`dropdown-arrow ${showCategoryDropdown ? 'rotated' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 9.5L12 15.5L6 9.5H18Z" fill="#FF6B00" stroke="#FF6B00" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
            
            {/* Category Dropdown Menu - Builder.io Design */}
            {showCategoryDropdown && (
              <div className="category-menu-wrapper" onClick={(e) => e.stopPropagation()}>
                <div className="category-menu figma-style">
                  <div className="category-menu-list">
                    {categories.map((category, index) => (
                      <div 
                        key={category.id || category.name} 
                        className={`category-menu-item ${hoveredCategory?.id === category.id ? 'active' : ''} ${index === 0 ? 'first-item' : ''}`}
                        onMouseEnter={() => setHoveredCategory(category)}
                        onClick={() => handleCategoryClick(category)}
                      >
                        <span className="category-menu-name">{category.name}</span>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M8 14.5L12 10.5L8 6.5" stroke={index === 0 || hoveredCategory?.id === category.id ? '#333333' : '#6D6D6D'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    ))}
                  </div>
                  <div className="category-menu-footer">
                    <NavLink to="/all-categories" className="see-more-link" onClick={() => setShowCategoryDropdown(false)}>
                      See more
                    </NavLink>
                  </div>
                </div>
                
                {/* Subcategory Panel - Same style as category menu */}
                {hoveredCategory && hoveredCategory.subcategories && hoveredCategory.subcategories.length > 0 && (
                  <div className="subcategory-panel figma-style">
                    <div className="subcategory-header">
                      <h4>{hoveredCategory.name}</h4>
                    </div>
                    <div className="subcategory-list">
                      {hoveredCategory.subcategories.map((sub, index) => (
                        <div 
                          key={sub.id || index} 
                          className="subcategory-item"
                          onClick={() => handleSubcategoryClick(hoveredCategory, sub)}
                        >
                          <span>{sub.name || sub}</span>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M8 14.5L12 10.5L8 6.5" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      ))}
                    </div>
                    <div className="subcategory-footer">
                      <span 
                        className="view-all-link"
                        onClick={() => handleCategoryClick(hoveredCategory)}
                      >
                        View all in {hoveredCategory.name} →
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="nav-links">
            <NavLink to="/buyer-directory" className={({ isActive }) => `nav-link has-dropdown ${isActive ? 'active' : ''}`}>Buyer Center <svg className="nav-arrow" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2.5 4L5 6.5L7.5 4" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></NavLink>
            <NavLink to="/top-seller" className={({ isActive }) => `nav-link has-dropdown ${isActive ? 'active' : ''}`}>Seller Center <svg className="nav-arrow" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2.5 4L5 6.5L7.5 4" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></NavLink>
            <NavLink to="/projects" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>Trade Shows</NavLink>
            <NavLink to="/news" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>News</NavLink>
            <NavLink to="/specialists" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>Channel Partner</NavLink>
            <NavLink to="/send-inquire" className={({ isActive }) => `nav-link has-dropdown ${isActive ? 'active' : ''}`}>Help Center <svg className="nav-arrow" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2.5 4L5 6.5L7.5 4" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></NavLink>
          </div>
          

        </div>
        
        {/* Homepage Hero Row - Directly below navbar, shares same container for perfect alignment */}
        {isHomePage && (
          <div className="homepage-hero-row">
            {/* Category Sidebar - Inline version for homepage */}
            <div className="header-category-sidebar">
              <div className="category-list">
                {categories.slice(0, 8).map((category, index) => (
                  <div 
                    key={category.id || category.name} 
                    className="category-item"
                    onClick={() => handleCategoryClick(category)}
                  >
                    <span className="category-name">{category.name}</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M8 14.5L12 10.5L8 6.5" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                ))}
              </div>
              <NavLink to="/all-categories" className="see-all-link">See all categories</NavLink>
            </div>
            
            {/* Placeholder for banner - actual banner in HomePage */}
            <div className="header-hero-placeholder"></div>
            
            {/* Placeholder for CTA - actual CTA in HomePage */}
            <div className="header-cta-placeholder"></div>
          </div>
        )}
      </nav>

      {/* Login Modal */}
      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={() => {
          closeModals();
          setLoginRedirectPath(null);
        }}
        onRegisterClick={openRegisterModal}
        redirectTo={loginRedirectPath}
      />

      {/* Register Modal */}
      <RegisterModal 
        isOpen={isRegisterModalOpen} 
        onClose={closeModals}
        onLoginClick={openLoginModal}
        onVerifyEmail={handleVerifyEmail}
      />

      {/* Verify Email Modal */}
      <VerifyEmailModal
        isOpen={isVerifyEmailModalOpen}
        onClose={closeModals}
        onBack={openRegisterModal}
        onContinue={handleVerifyEmailContinue}
      />

      {/* Requirement Modal (RFQ) */}
      <RequirementModal
        isOpen={isRFQModalOpen}
        onClose={closeRFQModal}
      />
    </header>
  );
};

export default Header;
